<script setup lang="ts">
import { AssetType, RelationshipType } from '@/helpers/const';
import { getFullName } from '@/helpers/format';
import { useDeclarationProcess } from '@/helpers/useDeclarationProcess';
import { useApplicantPermissions } from '@/helpers/useApplicantPermissions';
import type { ApplicantId, PropertyAsset } from '@/types';
import { useTemplateRefsList } from '@vueuse/core';
import { cloneDeep } from 'lodash-es';
import { ref } from 'vue';
import AssetForm from './modules/Assets/AssetForm.vue';
import { toast } from '@/helpers/toast';

const { loanApp, loanApplicants, saveAndGoNext, goBack } = useDeclarationProcess();
const { getApplicantsForAssetsDisplay, canEditApplicantAssets, canViewApplicantAssetsData } = useApplicantPermissions();

const getApplicantIndex = (id: string) => {
  return loanApp.value.applicants.findIndex((item) => item.id === id);
};

// Use permissions composable for visible applicants (only current user for non-broker)
const validApplicants = getApplicantsForAssetsDisplay;

const financials = ref(cloneDeep(loanApp.value.financials));
const assetRefs = useTemplateRefsList<InstanceType<typeof AssetForm>>();
const activeTab = ref<ApplicantId | undefined>(
  validApplicants.value.length === 1 ? validApplicants.value[0] : undefined,
);

const hasAssetsOrLiabilities = (applicantId: ApplicantId) => {
  const hasAssets = (financials.value[applicantId]?.assets?.length ?? 0) > 0;
  const hasLiabilities = (financials.value[applicantId]?.liabilities?.length ?? 0) > 0;
  return hasAssets || hasLiabilities;
};

const validateAssets = async () => {
  let isValid = true;
  // Build array of editable applicants in the same order refs are collected in the template
  const editableApplicants = validApplicants.value.filter((id) => canEditApplicantAssets(id));
  for (let i = 0; i < editableApplicants.length; i++) {
    isValid = (await assetRefs.value[i].update()) ?? false;
    if (!isValid) {
      window.scrollTo(0, 0);
      activeTab.value = editableApplicants[i];
      break;
    }
  }

  // Check for duplicate property addresses across all visible applicants
  const addressLines = validApplicants.value
    .filter((id) => canViewApplicantAssetsData(id))
    .flatMap((id) =>
      (financials.value[id]?.assets ?? [])
        .filter((asset) => asset.type === 'property' && asset.address)
        .map((asset) => (asset as PropertyAsset).address.addressLine),
    );

  const hasDuplicateAddress = addressLines.some((addressLine, index) => addressLines.indexOf(addressLine) !== index);
  if (hasDuplicateAddress) {
    isValid = false;
    window.scrollTo(0, 0);
    toast({
      type: 'danger',
      title: 'Error',
      description: 'Please only enter a property asset once.',
    });
  }

  return isValid;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const cardTitle = (item: any) => {
  let description = '';
  if (item.type === AssetType.PROPERTY) {
    description = item.address?.addressLine;
  } else if (item.type === AssetType.MOTOR_VEHICLES) {
    description = `${item.make ?? ''} ${item.model ?? ''}`.trim();
  } else if (item.type === AssetType.MACHINERY || item.type === AssetType.EQUIPMENT) {
    description = item.description ?? '';
  } else if (item.institution) {
    description = item.institution;
  } else if (item.model) {
    description = `${item.make} ${item.model}`;
  } else if (item.details) {
    description = item.details;
  }

  return `${item.type.charAt(0).toUpperCase() + item.type.slice(1).replace('_', ' ')}: ${description}`;
};

const cardDescription = (applicantId: ApplicantId) => {
  // Only show descriptions if user can view this applicant's data
  if (!canViewApplicantAssetsData(applicantId)) {
    return [];
  }

  const assets = (financials.value[applicantId]?.assets ?? []).map((item) => {
    return cardTitle(item);
  });
  const liabilities = (financials.value[applicantId]?.liabilities ?? []).map((item) => {
    return cardTitle(item);
  });

  return [...assets, ...liabilities];
};

const saveAndContinue = async () => {
  if (await validateAssets()) {
    saveAndGoNext({
      financials: financials.value,
    });
  }
};
</script>

<template>
  <div class="w-full">
    <div class="mb-4 pb-2">
      <h2 class="text-2xl font-medium">
        What
        <BxHighlight>assets</BxHighlight>
        and
        <BxHighlight>liabilities</BxHighlight>
        do you have?
      </h2>
    </div>

    <div v-if="loanApp.applicants.length > 1" class="my-6">
      <p
        v-if="
          loanApp.applicants[1].relationshipToPrimary === RelationshipType.MARRIED_OR_DEFACTO &&
          validApplicants.length > 1
        "
      >
        Please only include combined assets and liabilities once.
      </p>
      <p v-else-if="loanApp.actionType === 'createNew'">
        Only include your assets and liabilities, we will request this information from
        {{ loanApp.applicants[1].preferredName }} via email.
      </p>
    </div>

    <div class="my-6 grid gap-4">
      <div v-for="applicantId in validApplicants" :key="applicantId">
        <!-- Expanded Form View - only show if user can edit this applicant's data -->
        <BxCard v-if="canEditApplicantAssets(applicantId)" :class="applicantId !== activeTab && 'hidden'">
          <h2 class="mb-4 text-xl font-medium">
            {{ getFullName(loanApplicants[applicantId]) }}
          </h2>
          <AssetForm
            :ref="assetRefs.set"
            v-model:assets="financials[applicantId].assets"
            v-model:liabilities="financials[applicantId].liabilities"
            :applicant-id="applicantId"
            :animate="applicantId === activeTab"
            :show-buttons="validApplicants.length > 1"
            :disabled="!canEditApplicantAssets(applicantId)"
            @save="activeTab = undefined"
          />
        </BxCard>

        <!-- Collapsed Card View (Desktop) -->
        <BxCard :class="`${applicantId === activeTab && 'hidden'} asset-card`">
          <div class="flex items-center">
            <div class="card-details">
              <div class="mb-2 font-medium">{{ getFullName(loanApplicants[applicantId]) }}</div>
            </div>
            <!-- Only show asset descriptions if user can view this applicant's data -->
            <div v-if="canViewApplicantAssetsData(applicantId)" class="asset-descriptions ml-6">
              <div v-for="description in cardDescription(applicantId)" :key="description">
                <span class="text-gray-400">{{ description }}</span>
              </div>
            </div>

            <!-- Only show button if user can edit this applicant's assets -->
            <div v-if="canEditApplicantAssets(applicantId)" class="card-button">
              <BxButton
                :id="`applicant-${getApplicantIndex(applicantId)}-edit-or-add-assets`"
                variant="secondary"
                :disabled="activeTab && activeTab !== applicantId"
                @click="activeTab = applicantId"
              >
                {{ hasAssetsOrLiabilities(applicantId) ? 'Edit' : 'Add' }} assets & liabilities
              </BxButton>
            </div>
          </div>
        </BxCard>

        <!-- Collapsed Card View (Mobile) -->
        <BxCard :class="`${applicantId === activeTab && 'hidden'} asset-card-mobile`">
          <div class="items-left flex flex-col">
            <div class="card-details">
              <div class="mb-2 font-medium">{{ getFullName(loanApplicants[applicantId]) }}</div>
            </div>
            <!-- Only show asset descriptions if user can view this applicant's data -->
            <div v-if="canViewApplicantAssetsData(applicantId)" class="asset-descriptions ml-6">
              <div v-for="description in cardDescription(applicantId)" :key="description">
                <span class="text-gray-400">{{ description }}</span>
              </div>
            </div>
          </div>
          <!-- Only show button if user can edit this applicant's assets -->
          <div v-if="canEditApplicantAssets(applicantId)" class="card-button">
            <BxButton
              variant="secondary"
              :disabled="activeTab && activeTab !== applicantId"
              @click="activeTab = applicantId"
            >
              {{ hasAssetsOrLiabilities(applicantId) ? 'Edit' : 'Add' }} assets & liabilities
            </BxButton>
          </div>
        </BxCard>
      </div>
    </div>

    <div class="mt-6">
      <BxButton id="assets-continue" variant="primary" @click="saveAndContinue()">Save and continue</BxButton>
      <BxButton variant="link" class="ml-4" @click="goBack()">Back</BxButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.asset-descriptions {
  @media (max-width: 767px) {
    margin-top: 20px;
    margin-bottom: 20px;
  }
}

.asset-card {
  @media (max-width: 767px) {
    display: none;
  }
}

.asset-card-mobile {
  @media (min-width: 767px) {
    display: none;
  }
}

.card-button {
  flex-grow: 1;
  @media (min-width: 767px) {
    text-align: right;
  }
  @media (max-width: 767px) {
    text-align: center;
  }
  flex-basis: 200px;
}
</style>
